/**
 * Turning the forecast's posterior summaries into something readable.
 *
 * The Bayesian layer of the forecast pipeline has always returned these - the
 * backend computes median/p10/p90 per variable and ships them as
 * `posteriorSummaries` - and no view has ever rendered them, in v1 or v2. The
 * Monte Carlo OUTPUT range was visible; the posteriors that produced it were
 * not. So you could see that the valuation was uncertain but not what the
 * model believed about growth, headcount cost or capex intensity.
 *
 * The keys arrive namespaced by forecaster and, usually, suffixed by year:
 *
 *     revenue.growth.2025            a fraction  (0.12 = 12%)
 *     staffing.rev_per_fte.2026      money
 *     staffing.cph.2026              money
 *     capex.intensity.2025           a fraction of revenue
 *     opex.marketing.pct_of_revenue  a fraction, no year
 *
 * Parsing lives here rather than in the component because the shape is the
 * awkward part - a year that may or may not be present, a cost line that may
 * or may not be present - and that is worth testing directly.
 */

/** What the backend ships per variable. `n` is the sample count. */
export interface PosteriorSummary {
  median: number;
  p10: number;
  p90: number;
  n: number;
}

export type PosteriorUnit = 'percent' | 'money';

export interface PosteriorRow {
  /** The year, or '' for a variable that is not per-year. */
  label: string;
  median: number;
  p10: number;
  p90: number;
  n: number;
  /** p10 and p90 as percentages across the group's own range, for a bar. */
  barStart: number;
  barEnd: number;
  /** The median's position in the same scale. */
  barMedian: number;
}

export interface PosteriorGroup {
  /** Stable identity: forecaster + quantity path. */
  key: string;
  forecaster: string;
  title: string;
  unit: PosteriorUnit;
  rows: PosteriorRow[];
}

/**
 * Quantities the pipeline emits, and how to read them.
 *
 * Anything unrecognised is still shown - a new forecaster should surface
 * rather than vanish - and falls back to a percentage, which is what most of
 * these are. The label is then the raw name, which is ugly but honest.
 */
const QUANTITIES: Record<string, { title: string; unit: PosteriorUnit }> = {
  growth: { title: 'Revenue growth', unit: 'percent' },
  rev_per_fte: { title: 'Revenue per FTE', unit: 'money' },
  cph: { title: 'Cost per head', unit: 'money' },
  cost_per_head: { title: 'Cost per head', unit: 'money' },
  intensity: { title: 'Capex intensity', unit: 'percent' },
  pct_of_revenue: { title: 'Share of revenue', unit: 'percent' },
  cpi_sensitivity: { title: 'CPI sensitivity', unit: 'percent' },
};

/**
 * A key segment as a human would write it.
 *
 * The cost lines arrive as the forecaster's own identifiers, which are
 * camelCase: production showed "BankCharges", "OfficeRent", "TravelCosts" and
 * "WebsiteDesign" sitting in the panel exactly like that. Splitting on the
 * case boundary gives "Bank charges". Runs of capitals are kept whole so an
 * acronym stays an acronym rather than becoming "C R M".
 */
function prettify(segment: string): string {
  return (
    segment
      .replace(/[_.]/g, ' ')
      // camelCase and PascalCase boundaries, but not inside an acronym.
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .trim()
      // Sentence case, leaving an all-capitals word (CRM, VAT) alone.
      .split(' ')
      .map((word, index) => {
        if (word === word.toUpperCase()) return word;
        return index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word.toLowerCase();
      })
      .join(' ')
  );
}

function isYear(segment: string): boolean {
  return /^(19|20)\d{2}$/.test(segment);
}

/**
 * Group the flat `posteriorSummaries` map into one block per variable.
 *
 * Groups come back in a fixed order - revenue, staffing, opex, capex, then
 * anything new - so the panel does not reshuffle between runs. Within a group,
 * rows are in year order.
 */
export function groupPosteriors(
  summaries: Record<string, Record<string, number>> | undefined,
): PosteriorGroup[] {
  if (!summaries) return [];

  const groups = new Map<string, PosteriorGroup>();

  for (const [key, raw] of Object.entries(summaries)) {
    if (!raw || typeof raw.median !== 'number') continue;

    const segments = key.split('.').filter(Boolean);
    if (segments.length < 2) continue;

    const [forecaster, ...rest] = segments;
    const last = rest[rest.length - 1];
    const year = isYear(last) ? last : '';
    const quantityPath = year ? rest.slice(0, -1) : rest;
    if (quantityPath.length === 0) continue;

    const quantity = quantityPath[quantityPath.length - 1];
    const line = quantityPath.slice(0, -1).map(prettify).join(' ');
    const known = QUANTITIES[quantity];
    const title = [line, known ? known.title : prettify(quantity)]
      .filter(Boolean)
      .join(' — ');

    const groupKey = `${forecaster}.${quantityPath.join('.')}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        forecaster,
        title,
        unit: known ? known.unit : 'percent',
        rows: [],
      });
    }

    groups.get(groupKey)!.rows.push({
      label: year,
      median: raw.median,
      p10: raw.p10,
      p90: raw.p90,
      n: raw.n ?? 0,
      // Filled in below, once the group's range is known.
      barStart: 0,
      barEnd: 0,
      barMedian: 0,
    });
  }

  const ORDER = ['revenue', 'staffing', 'opex', 'capex'];
  const ordered = [...groups.values()].sort((a, b) => {
    const ai = ORDER.indexOf(a.forecaster);
    const bi = ORDER.indexOf(b.forecaster);
    const aRank = ai === -1 ? ORDER.length : ai;
    const bRank = bi === -1 ? ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return a.key.localeCompare(b.key);
  });

  for (const group of ordered) {
    group.rows.sort((a, b) => a.label.localeCompare(b.label));
    scaleBars(group);
  }

  return ordered;
}

/**
 * Position each row's interval on a scale shared by the whole group.
 *
 * Shared, so the bars are comparable down the column - a year whose interval
 * is twice as wide must LOOK twice as wide. A per-row scale would make every
 * interval fill the width and say nothing.
 *
 * A group whose values are all identical has no range. Such a bar collapses to
 * a point in the middle rather than filling the width: a full-width bar reads
 * as MAXIMUM uncertainty, which is the exact opposite of what a posterior with
 * no spread means.
 */
function scaleBars(group: PosteriorGroup): void {
  const lows = group.rows.map((r) => Math.min(r.p10, r.median, r.p90));
  const highs = group.rows.map((r) => Math.max(r.p10, r.median, r.p90));
  const min = Math.min(...lows);
  const max = Math.max(...highs);
  const span = max - min;

  for (const row of group.rows) {
    if (span === 0) {
      row.barStart = 50;
      row.barEnd = 50;
      row.barMedian = 50;
      continue;
    }
    row.barStart = ((row.p10 - min) / span) * 100;
    row.barEnd = ((row.p90 - min) / span) * 100;
    row.barMedian = ((row.median - min) / span) * 100;
  }
}

/**
 * A posterior value in its own units.
 *
 * Money is delegated to the caller, because the currency lives on the model
 * and this module has no business knowing it.
 */
export function formatPosterior(
  value: number,
  unit: PosteriorUnit,
  money: (v: number) => string,
): string {
  if (unit === 'money') return money(value);
  return `${(value * 100).toFixed(1)}%`;
}
